import React, { useState, useEffect } from 'react';
import Header from './Header';
import Navigation from './Navigation';
import MainDashboard from './MainDashboard';
import BusinessCyclesDashboard from './BusinessCyclesDashboard';
import DocumentSelector from './DocumentSelector';
import PlaceholderView from './PlaceholderView';
import JournalView from './JournalView';
import ChartOfAccountsView from './ChartOfAccountsView';
import InventoryView from './InventoryView';
import ProductionView from './ProductionView';
import QuotationView from './QuotationView';
import SalesOrderView from './SalesOrderView';
import MRPView from './MRPView';
import PurchaseOrderView from './PurchaseOrderView';
import QualityControlView from './qualityControl/QualityControlView';
import CostAccountingView from './costAccounting/CostAccountingView';
import FinancialManagementView from './financialManagement/FinancialManagementView';
import ProcessFlowTracker from './ProcessFlowTracker';
import TypicalFlowFeatures from './TypicalFlowFeatures';
import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import { TextSizeProvider } from '../contexts/TextSizeContext';
import Breadcrumb, { QuickNavMenu } from './ui/breadcrumb';
import { supabase } from '../lib/supabase';
import { Menu } from 'lucide-react';

// Inner component that uses navigation context
const BusinessDocumentSystemInner = () => {
  const [currentPeriod] = useState('2025-01');
  const [periodStatus] = useState('open');
  const [selectedCycle, setSelectedCycle] = useState('money');
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: 'ERPOkay 企業資源管理系統',
    establishedYear: '2020',
    employees: 0,
    revenue: 'HK$ 0'
  });
  const [loading, setLoading] = useState(true);
  
  // Use navigation context
  const { 
    currentTab, 
    navigateTo, 
    goBack, 
    breadcrumbPath, 
    cameFrom 
  } = useNavigation();

  // Fetch company info from Supabase
  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      
      // Fetch employees count
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('*');

      if (employeesError) throw employeesError;

      // Fetch total revenue from chart of accounts
      const { data: chartData, error: chartError } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('account_type', 'Revenue');

      if (chartError) throw chartError;

      const totalRevenue = chartData?.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0) || 0;

      setCompanyInfo({
        companyName: 'ERPOkay 企業資源管理系統',
        establishedYear: '2020',
        employees: employeesData?.length || 0,
        revenue: `HK$${(totalRevenue || 0).toLocaleString()}`
      });

    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: '主控台', icon: 'Home', category: 'dashboard' },
    { id: 'workflows', label: '業務流程', icon: 'Activity', category: 'dashboard' },
    { id: 'process-flow-tracker', label: '流程追蹤器', icon: 'Activity', category: 'dashboard' },
    { id: 'typical-flow-features', label: '典型流程學習', icon: 'BookOpen', category: 'dashboard' },
    { id: 'quotation', label: '報價單', icon: 'FileText', category: 'documents' },
    { id: 'sales-orders', label: '銷售訂單', icon: 'TrendingUp', category: 'orders' },
    { id: 'purchase-orders', label: '採購訂單', icon: 'ShoppingCart', category: 'orders' },
    { id: 'mrp', label: '物料需求計劃', icon: 'Layers', category: 'production' },
    { id: 'inventory', label: '庫存管理', icon: 'Boxes', category: 'production' },
    { id: 'production', label: '生產管理', icon: 'Factory', category: 'production' },
    { id: 'quality-control', label: '品質控制', icon: 'ClipboardCheck', category: 'production' },
    { id: 'financial-management', label: '財務管理', icon: 'DollarSign', category: 'accounting' },
    { id: 'cost-accounting', label: '成本會計', icon: 'BarChart2', category: 'accounting' },
    { id: 'journal', label: '日記帳', icon: 'BookOpen', category: 'accounting' },
    { id: 'chart-of-accounts', label: '會計科目表', icon: 'Calculator', category: 'accounting' }
  ];

  // Enhanced setActiveTab that uses navigation context
  const handleTabChange = (tabId, source = null) => {
    navigateTo(tabId, source);
  };

  const renderContent = () => {
    switch(currentTab) {
      case 'dashboard':
        return <MainDashboard />;
      case 'workflows':
        return (
          <BusinessCyclesDashboard 
            selectedCycle={selectedCycle}
            setSelectedCycle={setSelectedCycle}
            setActiveTab={handleTabChange}
          />
        );
      case 'process-flow-tracker':
        return <ProcessFlowTracker />;
      case 'typical-flow-features':
        return <TypicalFlowFeatures />;
      case 'chart-of-accounts':
        return <ChartOfAccountsView />;
      case 'journal':
        return <JournalView />;
      case 'inventory':
        return <InventoryView navigationSource={cameFrom('workflows') ? 'workflows' : null} />;
      case 'production':
        return <ProductionView navigationSource={cameFrom('workflows') ? 'workflows' : null} />;
      case 'quotation':
        return <QuotationView />;
      case 'sales-orders':
        return <SalesOrderView />;
      case 'purchase-orders':
        return <PurchaseOrderView />;
      case 'mrp':
        return <MRPView navigationSource={cameFrom('workflows') ? 'workflows' : null} />;
      case 'quality-control':
        return <QualityControlView />;
      case 'financial-management':
        return <FinancialManagementView />;
      case 'cost-accounting':
        return <CostAccountingView />;
      default:
        return <PlaceholderView title={tabs.find(t => t.id === currentTab)?.label} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading system...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header 
        companyInfo={companyInfo}
        currentPeriod={currentPeriod}
        periodStatus={periodStatus}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Navigation 
          tabs={tabs}
          activeTab={currentTab}
          setActiveTab={handleTabChange}
        />

        {/* Enhanced Navigation Bar */}
        <div className="mb-6 flex items-center justify-between bg-white rounded-lg shadow-sm p-4">
          <Breadcrumb 
            items={breadcrumbPath}
            onNavigate={(href) => handleTabChange(href)}
            onBack={goBack}
            showBackButton={breadcrumbPath.length > 1}
          />
          
          <button
            onClick={() => setShowQuickNav(true)}
            className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            title="快速導航"
          >
            <Menu className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">快速導航</span>
          </button>
        </div>

        {/* Document Selector */}
        {tabs.find(t => t.id === currentTab)?.category === 'documents' && (
          <DocumentSelector />
        )}
        
        {/* Orders Selector - For future expansion */}
        {tabs.find(t => t.id === currentTab)?.category === 'orders' && (
          <div className="mb-4"></div>
        )}

        {/* Main Content */}
        {renderContent()}
      </div>

      {/* Quick Navigation Menu */}
      <QuickNavMenu 
        isOpen={showQuickNav}
        onClose={() => setShowQuickNav(false)}
        onNavigate={handleTabChange}
        currentContext={currentTab}
      />
    </div>
  );
};

// Main component with navigation and text size providers
const BusinessDocumentSystem = () => {
  return (
    <TextSizeProvider>
      <NavigationProvider>
        <BusinessDocumentSystemInner />
      </NavigationProvider>
    </TextSizeProvider>
  );
};

export default BusinessDocumentSystem; 